import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LayoutDashboard, Lightbulb, User, Shield, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_300,w_300,f_auto,q_auto/17784585/525540_804907.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isLanding = location.pathname === "/";
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setMobileOpen(false);
  };

  const navLinks = user
    ? [
        { label: "Portfolio", to: "/portfolio", icon: null },
        { label: "Ideas", to: "/ideas", icon: Lightbulb },
        { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
      ]
    : [{ label: "Portfolio", to: "/portfolio", icon: null }];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 glass-nav transition-all duration-300 ${
        isLanding ? "" : "shadow-sm"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="SupraLife" className="h-8 w-8" />
            <span className="font-serif text-lg font-bold text-slate-900 tracking-tight hidden sm:block">
              SupraLife<span className="text-slate-400 font-normal">.Ventures</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-sans text-sm transition-colors duration-200 ${
                  location.pathname === link.to
                    ? "text-slate-900 font-medium"
                    : "text-slate-500 hover:text-slate-900"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}

            {user ? (
              <div className="flex items-center gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button variant="outline" size="sm" className="gap-2 border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600">
                      <Shield className="w-3.5 h-3.5" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to="/profile" data-testid="nav-profile-link">
                  <Button variant="ghost" size="sm" className="gap-2 text-slate-600">
                    <User className="w-3.5 h-3.5" />
                    {user.name?.split(" ")[0]}
                  </Button>
                </Link>
                <Button
                  variant="ghost" size="sm"
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-red-500"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/auth" data-testid="nav-login-link">
                  <Button variant="ghost" size="sm" className="text-slate-600 font-sans">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth?tab=register" data-testid="nav-register-link">
                  <Button
                    size="sm"
                    className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans transition-all duration-300"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="block py-2 text-sm text-slate-600 hover:text-slate-900"
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-mobile-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="block py-2 text-sm text-blue-600" onClick={() => setMobileOpen(false)}>
                  Admin Panel
                </Link>
              )}
              <Link to="/profile" className="block py-2 text-sm text-slate-600" onClick={() => setMobileOpen(false)}>
                Profile
              </Link>
              <button onClick={handleLogout} className="block py-2 text-sm text-red-500">
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/auth" className="block py-2 text-sm text-slate-600" onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
              <Link to="/auth?tab=register" className="block py-2 text-sm font-medium text-blue-600" onClick={() => setMobileOpen(false)}>
                Get Started
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
